import { vsprintf } from "sprintf-js";
import { dataDogLogger } from "@src/lib/datadog/initDatadogLogs";
import LocalStorageModel from "../prototypes/LocalStorageModel";

//
// Model Translation
//

// Dédup global (durée de vie de la session) : une clé manquante n'est
// remontée à DataDog qu'une seule fois, tous appels confondus.
const reportedMissingKeys = new Set<string>();

// Clés demandées avant l'injection du TranslationModel du setup
// (app.t appelé sur le modèle vide de fallback, cf. AppModel.getTranslations).
// Vérifiées au chargement des traductions (flushPendingMissingKeys) pour
// éviter les faux positifs dûs au chargement différé du setup.
const pendingMissingKeys = new Map<
  string,
  { key: string; context: string; locale: string }
>();

// Dédup global des appels app.t avant setup (mésusage) : un même appel
// n'est signalé qu'une seule fois par session.
const reportedEarlyCalls = new Set<string>();

// app.t ne devrait jamais être appelé avant l'injection des traductions du
// setup : on le signale comme mésusage (notice), sans préjuger de la clé.
const reportEarlyCall = (locale: string, context: string, key: string) => {
  const dedupKey = `${locale}:${context}:${key}`;
  if (reportedEarlyCalls.has(dedupKey)) return;
  reportedEarlyCalls.add(dedupKey);
  dataDogLogger.notice(
    `Translation called before setup in \`${context}\` for key \`${key}\``,
    {
      key,
      context,
      locale,
      fn: "app.t",
    }
  );
};

const reportMissingKey = (locale: string, context: string, key: string) => {
  const dedupKey = `${locale}:${context}:${key}`;
  if (reportedMissingKeys.has(dedupKey)) return;
  reportedMissingKeys.add(dedupKey);
  dataDogLogger.warn(
    `Missing translation ${locale} in \`${context}\` for key \`${key}\``,
    {
      key,
      context,
      locale,
      fn: "app.t",
    }
  );
};

class TranslationModel extends LocalStorageModel {
  name = "TranslationModel";
  route = "apiTranslationModel";

  // le modèle de fallback (avant setup) ne contient aucun contexte
  isLoaded() {
    return Object.keys(this.attributes).length > 0;
  }

  getTranslation(key: string, context: string): string | undefined {
    let contextBase: Record<string, string> = this.get(context);
    if (!contextBase) contextBase = this.get("default") || {};
    return contextBase[key];
  }

  // Revérifie les clés demandées avant le chargement des traductions :
  // seules celles toujours absentes du modèle chargé sont remontées.
  static flushPendingMissingKeys(model: TranslationModel) {
    if (!model.isLoaded()) return;
    pendingMissingKeys.forEach(({ key, context, locale }) => {
      if (!model.getTranslation(key, context)) {
        reportMissingKey(locale, context, key);
      }
    });
    pendingMissingKeys.clear();
  }

  translate(
    key: string,
    context: string,
    parameters?: string | number | boolean | Array<string | number | boolean>
  ): string {
    let translation = this.getTranslation(key, context);
    // si la traduction n'existe pas, on retourne la clé de traduction
    if (!translation) {
      const locale =
        typeof window !== "undefined"
          ? (window as any).___gatsbyIntl?.language
          : undefined;
      // le français est la langue par défaut des clés, il faut donc l'ignorer
      // dans le tracking des clés manquantes
      if (locale && locale !== "fr") {
        if (this.isLoaded()) {
          reportMissingKey(locale, context, key);
        } else {
          reportEarlyCall(locale, context, key);
          pendingMissingKeys.set(`${locale}:${context}:${key}`, {
            key,
            context,
            locale,
          });
        }
      }
      translation = key;
    }

    // Traduction simple sans paramètres
    if (!parameters) {
      return translation;
    }
    //
    switch (typeof parameters) {
      case "boolean":
      case "string":
      case "number":
        const string = parameters.toString();
        parameters = [];
        parameters.push(string);
        break;
    }
    //
    //
    try {
      const r = vsprintf(translation, parameters);
      return r;
    } catch (err) {
      return translation;
    }
  }
}

export default TranslationModel;
